import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import logger from '../../utils/logger';
import { EstimationLogo } from '../../assets/EstimationLogo';
import { Button } from '../ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { AlertCircle, Loader2, Mail, Lock } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';
// import { TurnstileCaptcha } from '../common/TurnstileCaptcha';

export function SupabaseLoginPage() {
  const navigate = useNavigate();
  const { user, login, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // const [turnstileToken, setTurnstileToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'email' | 'password' | 'captcha', string>>>({});

  // Session management is now handled entirely by SupabaseAuthContext
  // Removed automatic session clearing to prevent logout on refresh
  // The auth context will handle session validation and redirect logic

  const clearFieldError = (field: 'email' | 'password') => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setFieldErrors({});

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(trimmedEmail)) {
      setFieldErrors(prev => ({ ...prev, email: 'Please enter a valid email address.' }));
      setError('Please fix the highlighted errors and try again.');
      return;
    }

    if (!trimmedPassword) {
      setFieldErrors(prev => ({ ...prev, password: 'Password is required.' }));
      setError('Please fix the highlighted errors and try again.');
      return;
    }

    // REMOVED: Turnstile captcha temporarily disabled
    // if (!turnstileToken) {
    //   setFieldErrors(prev => ({ ...prev, captcha: 'Please complete the CAPTCHA verification.' }));
    //   setError('Please complete the CAPTCHA verification to continue.');
    //   return;
    // }

    setIsLoading(true);

    try {
      logger.debug('🔍 About to call login function');
      const result = await login(trimmedEmail, trimmedPassword, '');
      logger.debug('🔍 Login result:', logger.sanitize(result));

      if (result.error) {
        logger.debug('❌ Login page received error:', result.error);
        const normalized = result.error.toLowerCase();

        if (normalized.includes('email not confirmed')) {
          logger.debug('Setting email confirmation error');
          setError('Please check your email and confirm your account before logging in.');
        } else if (normalized.includes('invalid login credentials')) {
          logger.debug('Setting invalid credentials error');
          setError('Invalid email or password. Please try again.');
          setFieldErrors(prev => ({ ...prev, password: 'Email or password is incorrect.' }));
        } else if (normalized.includes('user not found')) {
          logger.debug('Setting user not found error');
          setError('We could not find an account with that email.');
          setFieldErrors(prev => ({ ...prev, email: 'No account found with this email address.' }));
        } else {
          logger.debug('Setting generic error');
          setError(result.error);
        }
        logger.debug('❌ Error state should be set, returning early');
        return;
      }

      // Login successful, navigate to dashboard
      const returnTo = localStorage.getItem('returnTo') || '/dashboard';
      localStorage.removeItem('returnTo');
      navigate(returnTo);
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setFieldErrors(prev => ({ ...prev, email: 'Enter your email so we can send a reset link.' }));
      setError('Please enter your email address first.');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await resetPassword(email);
      
      if (result.error) {
        setError(result.error);
      } else {
        setMessage('Check your email for the password reset link.');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-3">
              <EstimationLogo className="h-10 w-10" variant="dark" />
              <span className="text-3xl font-bold tracking-wider text-foreground transform skew-x-[-1deg]" style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif', fontWeight: '900' }}>
                ESTIMATION<span className="text-primary">.PK</span>
              </span>
            </div>
          </div>
          <p className="text-muted-foreground">Welcome back! Sign in to your account</p>
        </div>

        {/* Login Card */}
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl">Sign In</CardTitle>
            <CardDescription>
              Enter your credentials to access your dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      clearFieldError('email');
                      setEmail(e.target.value);
                    }}
                    required
                    disabled={isLoading}
                    autoComplete="email"
                    aria-invalid={fieldErrors.email ? 'true' : 'false'}
                    className={cn('pl-10', fieldErrors.email && 'border-destructive focus-visible:ring-destructive')}
                  />
                </div>
                {fieldErrors.email && (
                  <p className="text-xs text-destructive mt-1">{fieldErrors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      clearFieldError('password');
                      setPassword(e.target.value);
                    }}
                    required
                    disabled={isLoading}
                    autoComplete="current-password"
                    aria-invalid={fieldErrors.password ? 'true' : 'false'}
                    className={cn('pl-10', fieldErrors.password && 'border-destructive focus-visible:ring-destructive')}
                  />
                </div>
                {fieldErrors.password && (
                  <p className="text-xs text-destructive mt-1">{fieldErrors.password}</p>
                )}
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-sm"
                  onClick={handleForgotPassword}
                  disabled={isLoading}
                >
                  Forgot password?
                </Button>
              </div>

              {/* Turnstile CAPTCHA - TEMPORARILY DISABLED */}
              {/* <div className="space-y-2">
                <TurnstileCaptcha
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => {
                    setTurnstileToken('');
                    setFieldErrors(prev => ({ ...prev, captcha: 'CAPTCHA verification failed.' }));
                  }}
                  onExpire={() => {
                    setTurnstileToken('');
                    setFieldErrors(prev => ({ ...prev, captcha: 'CAPTCHA expired. Please try again.' }));
                  }}
                  theme="auto"
                  size="normal"
                />
                {fieldErrors.captcha && (
                  <p className="text-xs text-destructive mt-1">{fieldErrors.captcha}</p>
                )}
              </div> */}

              {/* Error Message */}
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Success Message */}
              {message && (
                <div className="bg-primary/10 border border-primary/20 text-primary rounded-lg p-3 text-sm">
                  {message}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-muted-foreground/20"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            {/* Sign Up Link - Disabled in invite-only mode */}
            {/* <div className="text-center text-sm">
              <span className="text-muted-foreground">Don&apos;t have an account? </span>
              <Link
                to="/signup"
                className="font-medium text-primary hover:underline"
              >
                Sign up for free
              </Link>
            </div> */}

            {/* Mock Auth Option (Development) */}
            {import.meta.env.VITE_ENABLE_MOCK_AUTH === 'true' && (
              <div className="mt-4 pt-4 border-t border-muted-foreground/20">
                <Link to="/mock-login">
                  <Button
                    variant="outline"
                    className="w-full"
                    type="button"
                  >
                    Use Mock Authentication (Dev)
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Back to Home Link */}
        <div className="text-center">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
